import type { RuntimeConfig } from './auth';

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export interface Api {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  putJson<T>(path: string, value: unknown): Promise<T>;
  putText<T>(path: string, body: string): Promise<T>;
}

export function makeApi(
  config: RuntimeConfig,
  getToken: () => string | null | Promise<string | null>,
): Api {
  const call = async <T>(path: string, init: RequestInit): Promise<T> => {
    // Await the getter: a sync (non-promise) return resolves immediately, so
    // sync call sites are unaffected; an async getter (ensureFreshToken)
    // refreshes-if-needed before the header is built.
    const token = await getToken();
    const res = await fetch(`${config.apiUrl}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        authorization: token ? `Bearer ${token}` : '',
      },
    });
    const text = await res.text();
    if (!res.ok) {
      let msg = text;
      try { msg = (JSON.parse(text) as { error?: string }).error ?? text; } catch { /* raw */ }
      throw new ApiError(res.status, msg);
    }
    return JSON.parse(text) as T;
  };
  return {
    get: (path) => call(path, { method: 'GET' }),
    // POST a JSON body (omitted when `body` is undefined — e.g. revoke, which
    // carries the id in the path). The mint/revoke handlers send/return JSON.
    post: (path, body) => call(path, {
      method: 'POST',
      ...(body === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    }),
    putJson: (path, value) => call(path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ value }),
    }),
    putText: (path, body) => call(path, {
      method: 'PUT',
      headers: { 'content-type': 'text/plain' },
      body,
    }),
  };
}
